import { observable, action, computed, makeObservable } from 'mobx'
import truncate from 'lodash/truncate'
import { QuestionDTO } from 'jobs/dtos/questions'
import { QuestionValidationResult } from './QuestionValidationResult'
import { DocumentRequestsViewModelPort } from 'documents/components/DocumentRequests/DocumentRequestsViewModel'
import { DateZero } from '../../utils/date-util'
import { QuestionInputValue } from './input-value-types'

/**
 * Callback for reporting a change of answer.
 */
export type NotifyCallback = () => unknown

/**
 * Question view model base.
 */
export default abstract class QuestionViewModelBase<T extends QuestionDTO> {
  /**
   * The question type.
   */
  type: T['type'] = null as any
  /**
   * Question ID.
   */
  id = ''
  /**
   * The question text.
   */
  @observable question = ''
  /**
   * The question description.
   */
  @observable description = ''
  /**
   * Any validation errors.
   */
  @observable validationErrors: string[] = []

  /**
   * Constructor.
   *
   * @param adapter Adapter for various components.
   */
  constructor(protected adapter: QuestionViewModelPort) {
    makeObservable(this)
  }

  /**
   * Calls `adapter.notify`.
   *
   * @protected
   */
  protected notify = () => this.adapter.notify()

  /**
   * Whether the question has been answered.
   * The answer MAY be invalid.
   */
  abstract get isAnswered(): boolean

  /**
   * Getter for a text-based representation of the answer.
   */
  protected abstract textAnswer(maxLength: number): string

  /**
   * Converts the answer to a text-based representation.
   * @param maxLength
   */
  toTextAnswer(maxLength?: number) {
    const text = this.textAnswer(maxLength || Infinity)
    if (maxLength === undefined) {
      return text
    }
    return truncate(text, {
      length: maxLength,
      separator: ', ',
      omission: '...',
    })
  }

  /**
   * The answer represented as a DTO.
   * Using a `computed` ensures it is cached.
   */
  @computed
  get answerDTO() {
    return {
      id: this.id,
      type: this.type,
      answer: this.serializeAnswer(),
    }
  }

  /**
   * Hydrates the Question DTO onto the viewmodel.
   * NOTE: This is NOT wrapped in an @action because
   * the caller should already do that. This saves a bit of overhead.
   * @param dto
   */
  hydrateDTO(dto: T) {
    this.id = dto.id
    this.question = dto.question || ''
    this.description = dto.description || ''
    this.type = dto.type
    this.hydrate(dto)
    this.hydrateAnswer(dto.answer)
    return this
  }

  /**
   * Validates the given response; populates the `validationErrors` array.
   */
  @action.bound
  validateResponse() {
    this.validationErrors = []
    const result = this.validate()
    if (result.type === 'Failed') {
      this.validationErrors.push(...result.messages)
    }
    return result
  }

  /**
   * Question-specific hydration. Can be overridden.
   *
   * @param _dto
   */
  protected hydrate(_dto: T): void {
    //
  }

  /**
   * Validates the current answer. Can be overriden.
   */
  validate(): QuestionValidationResult {
    return QuestionValidationResult.ok()
  }

  /**
   * Question-specific answer hydration.
   *
   * @param answer
   */
  protected abstract hydrateAnswer(answer: unknown): void

  /**
   * Serializes the answer.
   */
  protected abstract serializeAnswer(): unknown

  /**
   * Converts the question to QuestionInputValue
   */
  abstract toInputValue(): QuestionInputValue

  /**
   * Optional disposer. Override in impl, no need to call `super`.
   */
  dispose(): void {
    //
  }
}

/**
 * Ports for the question VM.
 */
export interface QuestionViewModelPort {
  /**
   * Used to notify the consumer that an answer was changed.
   * Not called with any arguments, the consumer can use `vm.answer`.
   */
  notify: NotifyCallback
  /**
   * Used by Documents Question.
   */
  documentRequestsAdapterFactory: () => DocumentRequestsViewModelPort
  /**
   * Used for date validations, the relative date to base before/after
   * validations off of.
   */
  getAsOfDate: () => Date
}

/**
 * Question VM Port helper.
 * @constructor
 */
export function QuestionViewModelAdapter(
  notify?: QuestionViewModelPort['notify'],
  getAsOfDate?: QuestionViewModelPort['getAsOfDate'],
  documentRequestsAdapterFactory?: QuestionViewModelPort['documentRequestsAdapterFactory'],
): QuestionViewModelPort {
  return {
    notify: notify ?? QuestionViewModelAdapter.none.notify,
    getAsOfDate: getAsOfDate ?? QuestionViewModelAdapter.none.getAsOfDate,
    documentRequestsAdapterFactory:
      documentRequestsAdapterFactory ??
      QuestionViewModelAdapter.none.documentRequestsAdapterFactory,
  }
}

/**
 * None instance.
 */
QuestionViewModelAdapter.none = {
  notify: () => {
    // no-op
  },
  getAsOfDate: () => DateZero(),
  documentRequestsAdapterFactory: () => {
    throw new Error(
      'The empty Question View Model port does not supply a document request adapter.',
    )
  },
} as QuestionViewModelPort
